import type { BlogPost } from "./posts";

export const creativePosts: BlogPost[] = [
  {
    slug: "mood-boards-infinite-canvas",
    title: "Mood Boards on an Infinite Canvas: A Designer's Guide",
    description:
      "How designers create digital mood boards on infinite canvases, including tips for sourcing, arranging, and presenting visual inspiration.",
    date: "2026-06-14",
    readTime: "7 min read",
    category: "Creative",
    content: `
## Why Mood Boards Still Matter in a Digital World

Mood boards have been a staple of creative work for decades. Interior designers pin fabric swatches to corkboards. Film directors assemble collages of reference stills. Brand strategists gather color palettes, typography samples, and competitor screenshots into a single visual field. The purpose is always the same: to **externalize a feeling** so that everyone on a project shares the same creative direction before a single pixel is placed or a single line is drawn.

Digital mood boards take this practice further. They remove the constraints of physical space, letting you zoom in on fine detail or zoom out to see the full picture. An infinite canvas is the ideal surface for this work because it imposes no edges, no page breaks, and no artificial grid. You arrange things the way your creative instincts tell you to, not the way a template forces you to. Mood boarding is one of many [visual brainstorming techniques](/blog/creative/visual-brainstorming-techniques) that work better in open space than in a list, and you can start one in seconds on a [free online whiteboard](/tools/whiteboard).

## Sourcing Inspiration Effectively

The quality of your mood board depends on the quality of what you put into it. Here are reliable sources for visual material:

- **Photography sites** like Unsplash, Pexels, and Are.na for high-resolution images
- **Design galleries** such as Dribbble, Behance, and Awwwards for UI and graphic design references
- **Social platforms** including Pinterest and Instagram for trend-spotting
- **Your own photography** and sketches, which add a personal and authentic dimension
- **Screenshots** of real products, packaging, signage, and environments you encounter

A common mistake is collecting too broadly. Set a constraint before you begin: a theme, a color family, a mood word like "serene" or "industrial." This filter keeps your board focused and useful rather than an overwhelming dump of pretty pictures.

## Arranging Your Board on an Infinite Canvas

Once you have raw material, arrangement is where the real thinking happens. Here are several layout strategies designers use:

### The Cluster Approach

Group images by sub-theme. For a brand identity project, you might have one cluster for color palette references, another for typography inspiration, and a third for photography style. Leave generous whitespace between clusters so each one reads as a distinct thought.

### The Spectrum Layout

Arrange images along a horizontal or vertical axis that represents a continuum. Left to right might go from "minimal" to "ornate," or top to bottom might move from "warm" to "cool." This layout is especially powerful for narrowing down direction with a client because it frames the conversation as a slider rather than a binary choice.

### The Narrative Flow

Lay out images in a rough sequence that tells a story. This works well for experiential design, event planning, or film pre-production where the viewer should feel a progression from one emotional beat to the next.

### Tips for Better Arrangement

1. Use consistent image sizes within a cluster to create visual harmony
2. Add short text annotations next to images explaining why each one is included
3. Draw arrows or lines connecting related pieces across different clusters
4. Include negative examples labeled "not this" to sharpen the direction

## Presenting Mood Boards to Stakeholders

A mood board that lives only in your head or your personal workspace is half-finished. Presentation is a critical step.

**Frame the narrative first.** Before showing the board, explain the brief, the audience, and the problem you are solving. Then reveal the board as your proposed emotional and visual direction.

**Walk through clusters or zones.** Do not just throw the whole board on screen and wait. Guide viewers through each section, explaining the reasoning behind your selections.

**Invite reaction, not critique.** At the mood board stage you want gut responses: "this feels right," "this feels too cold," "I love this energy." Detailed design critique comes later.

Tools like OmniCanvas make this workflow seamless because you can build the board, annotate it, and present it from the same infinite canvas without exporting to slides or PDFs. Everything stays in context.

## Maintaining Living Mood Boards

The best mood boards are not static artifacts. They evolve as the project evolves. Add new references as you discover them. Move things that no longer fit into an "archive" zone off to the side rather than deleting them. Date your additions so you can see how the creative direction shifted over time.

A living mood board becomes a **visual decision log** for your project. Months later, when someone asks why you chose a particular direction, you can zoom into the board and trace the evolution of the idea from raw inspiration to final concept. That is the power of working on an infinite canvas: nothing is lost, and everything has room to grow.
`,
  },
  {
    slug: "writers-spatial-notes-outlining",
    title: "How Writers Use Spatial Notes to Outline Stories",
    description:
      "Techniques for outlining novels, screenplays, and non-fiction using spatial canvas layouts including character maps, plot timelines, and scene cards.",
    date: "2026-06-21",
    readTime: "8 min read",
    category: "Creative",
    content: `
## The Problem with Linear Outlines

Most writers start outlining in a word processor or a bulleted list. Chapter one, chapter two, chapter three. This approach works for short, straightforward pieces, but it breaks down quickly for complex narratives. A novel has subplots that weave through dozens of chapters. A screenplay has character arcs that intersect at key turning points. A long-form non-fiction book has thematic threads that need to surface at just the right moments.

Linear outlines hide these connections. You can see what comes before and after a given point, but you cannot see what is happening **simultaneously** across different storylines. Spatial outlining solves this by letting you arrange story elements on a two-dimensional surface where proximity, grouping, and visual connections reveal the structure of your narrative at a glance. Spatial outlining is a writer's take on broader [visual brainstorming techniques](/blog/creative/visual-brainstorming-techniques), and a blank [online whiteboard](/tools/whiteboard) is all you need to try it.

## Building a Character Map

Start with your characters. Place each one on the canvas as a card or text block with their name, role, and a one-sentence description of their arc. Then draw lines between characters who have significant relationships. Label each line with the nature of the relationship: mentor, rival, love interest, foil.

This immediately reveals structural insights:

- **Isolated characters** with no connections might need stronger ties to the story
- **Overcrowded clusters** suggest you may have too many characters serving the same function
- **Missing connections** between subplots highlight opportunities for intersecting storylines

Position allied characters close together and antagonistic characters farther apart. The spatial arrangement itself becomes a visual summary of your story's social dynamics.

## Creating a Plot Timeline

Below or beside your character map, lay out a horizontal timeline. This is not a strict calendar (unless your story demands one) but a sequence of major story beats. Many writers use a framework like the three-act structure, the Save the Cat beat sheet, or the seven-point story structure as a starting skeleton.

Place each beat as a card on the timeline. Color-code cards by subplot or point-of-view character. For a novel with three POV characters, you might use blue, green, and orange cards. Scanning the timeline, you can instantly see whether one character disappears for too long or whether a subplot resolves too early.

### Tips for Effective Timelines

1. Keep beat descriptions short, no more than two sentences per card
2. Add vertical swim lanes if you have parallel storylines happening in different locations
3. Mark act breaks and midpoints with vertical divider lines
4. Place theme or motif notes above the timeline as a separate track

## Scene Cards and the Corkboard Method

Many screenwriters and novelists think in scenes. Each scene gets its own card containing:

- **Setting:** Where and when the scene takes place
- **Characters present:** Who appears in the scene
- **Goal:** What the protagonist wants in this scene
- **Conflict:** What stands in the way
- **Outcome:** How the scene ends and what changes
- **Emotional tone:** One or two words describing the feel

Arrange these cards in sequence on your canvas. The beauty of a spatial canvas is that you can drag scenes around to experiment with different orderings. Move a reveal earlier. Push a confrontation later. See how the emotional rhythm changes. This is far more intuitive than cutting and pasting paragraphs in a document.

On an infinite canvas like OmniCanvas, you can keep multiple versions of your scene order side by side, comparing them visually before committing to a final structure.

## World-Building Zones

For writers working in speculative fiction, fantasy, or science fiction, world-building generates enormous amounts of reference material: maps, magic systems, technology rules, cultural notes, language fragments. On a spatial canvas, you can dedicate an entire region to world-building, organized by category.

Place your story timeline in the center of the canvas. Surround it with world-building zones: geography to the north, culture to the east, technology to the south, history to the west. Draw connections from specific world-building notes to the scenes where they become relevant. This ensures your world-building serves the story rather than existing in isolation.

## The Research Shelf

Every long writing project accumulates research. Interview notes, article clippings, historical facts, expert quotes. Create a dedicated zone on your canvas for research material. Tag or color-code each piece by topic. When you need a specific fact for a scene, you know exactly where to look without leaving your outlining environment.

## From Spatial Outline to Draft

A spatial outline is not a replacement for the actual writing. It is a launchpad. When you sit down to draft a chapter, zoom into the relevant section of your canvas. Review the scene cards, check the character map for relationship context, glance at the timeline to confirm pacing. Then open your writing tool and draft with confidence, knowing the structural work is done.

The spatial outline remains a living document throughout the drafting and revision process. As your story evolves, update the canvas. Move cards, add new ones, draw new connections. The outline grows with the manuscript, serving as both map and memory for your creative work.
`,
  },
  {
    slug: "visual-brainstorming-techniques",
    title: "Visual Brainstorming Techniques That Beat Bullet Lists",
    description:
      "Hands-on brainstorming methods that use visual and spatial approaches, including brainwriting, SCAMPER, crazy eights, and affinity mapping.",
    date: "2026-06-28",
    readTime: "7 min read",
    category: "Creative",
    content: `
## Why Bullet Lists Fall Short

When most people brainstorm, they open a document and start typing a bulleted list. The first few ideas come quickly. Then the pace slows. The linear format creates an implicit hierarchy where early ideas feel more important simply because they appear first. Worse, each new idea must compete with the growing list for attention, and people unconsciously self-censor to avoid adding something that seems redundant or silly.

Visual brainstorming breaks these patterns. By spreading ideas across a two-dimensional space, you remove the implied ranking of a list. Ideas become **objects you can move, group, and connect** rather than lines you scroll past. Research in cognitive science consistently shows that spatial arrangement aids creative thinking by engaging different neural pathways than sequential text processing.

Here are five visual brainstorming techniques you can use on any infinite canvas, including OmniCanvas.

## 1. Mind Mapping

Mind mapping is the most familiar visual brainstorming method. Place your central question or topic in the middle of the canvas. Branch out with major categories. Branch again with sub-ideas. Use color to distinguish branches and keep the map scannable.

**Tips for better mind maps:**

- Limit each node to three to five words so the map stays visual rather than becoming a disguised outline
- Use images or simple icons alongside text when possible
- Draw cross-links between branches when ideas in different categories relate to each other
- Do not worry about neatness during the brainstorming phase; reorganize later

Mind maps work best for **divergent exploration** of a broad topic when you are not sure where the boundaries are.

## 2. Brainwriting (6-3-5 Method)

Brainwriting is a structured alternative to verbal brainstorming that works exceptionally well for teams. The classic format is called 6-3-5: six participants each write three ideas in five minutes, then pass their sheet to the next person who builds on those ideas.

On a canvas, adapt this method by creating six rectangular zones. Each participant adds three idea cards to their zone. After five minutes, everyone rotates to the next zone and adds new ideas inspired by what they see. After six rounds, you have up to 108 ideas generated with minimal groupthink.

**Why it works:** Brainwriting eliminates the loudest-voice-wins problem of verbal brainstorming. Introverted team members contribute equally. Building on others' ideas is baked into the process.

## 3. SCAMPER

SCAMPER is a checklist-based creativity technique that prompts you to think about an existing product, process, or idea from seven angles:

- **Substitute:** What components could you swap out?
- **Combine:** What could you merge with something else?
- **Adapt:** What could you borrow from a different domain?
- **Modify:** What could you enlarge, shrink, or reshape?
- **Put to other use:** What else could this be used for?
- **Eliminate:** What could you remove entirely?
- **Reverse:** What if you flipped the order or roles?

On a canvas, create seven zones labeled with each SCAMPER prompt. Place your starting concept in the center. Generate ideas in each zone. This method is particularly effective for **product innovation and feature ideation** because it forces you beyond your default thinking patterns.

## 4. Crazy Eights

Borrowed from design sprints, Crazy Eights is a rapid sketching exercise. Divide a region of your canvas into eight roughly equal sections. Set a timer for eight minutes. Sketch one idea per section, spending about one minute on each. The time pressure is the point. It forces you past your first, obvious ideas and into more creative territory.

**Keys to success:**

1. Sketch, do not write paragraphs; stick figures and boxes are fine
2. Do not evaluate ideas during the exercise
3. After finishing, mark your two or three strongest concepts with a star
4. Share your favorites with the group and discuss

Crazy Eights is ideal for **interface design, marketing concepts, and visual communication** challenges where you need to explore many directions quickly.

## 5. Affinity Mapping

Affinity mapping is both a brainstorming and an organization technique. Start by generating ideas individually, one per card or sticky note on the canvas. Aim for volume: 30 to 100 ideas depending on the scope of the problem. Then, as a group, silently sort the cards into clusters based on natural similarities. Do not pre-define categories. Let the groupings emerge organically.

Once clusters form, label each one with a theme. These themes often become your strategic priorities, feature categories, or project workstreams.

**When to use affinity mapping:**

- After user research interviews, to [synthesize patterns in feedback](/blog/creative/canvas-ux-research-synthesis)
- After a brainstorming session to organize raw ideas into actionable themes
- At the start of a project to define scope and priorities

## Choosing the Right Technique

No single method works for every situation. Use mind mapping when exploring a new space. Use brainwriting when you need equitable group participation. Use SCAMPER when innovating on an existing concept. Use Crazy Eights when you need visual concepts fast. Use affinity mapping when you have lots of raw input and need structure.

The common thread is that all of these methods benefit from a **spatial, visual workspace** where ideas can be freely arranged, grouped, and connected. Moving beyond the bullet list is the first step toward richer, more creative thinking. From there, the same canvas can hold a [creative project plan](/blog/creative/plan-creative-project-canvas) or a [mood board](/blog/creative/mood-boards-infinite-canvas) without ever leaving the workspace.
`,
  },
  {
    slug: "canvas-ux-research-synthesis",
    title: "How to Use a Canvas for UX Research Synthesis",
    description:
      "Practical guide to synthesizing user research data on a canvas, including affinity diagramming, journey mapping, and insight clustering.",
    date: "2026-07-05",
    readTime: "8 min read",
    category: "Creative",
    content: `
## The Synthesis Challenge

You have just completed a round of user research. Maybe it was eight usability tests, twelve stakeholder interviews, or a survey with hundreds of open-ended responses. You have pages of notes, recordings, and observations. Now what?

Synthesis is the process of transforming raw research data into **actionable insights** that drive design decisions. It is the hardest and most valuable part of UX research, and it is where most teams struggle. The difficulty is not intellectual; it is spatial. You need to see all your data at once, find patterns across participants, and build a coherent story. A linear document cannot support this. You need a canvas. Synthesis draws on the same [visual brainstorming techniques](/blog/creative/visual-brainstorming-techniques) that power ideation, run in reverse, and you can spread everything out on a [free online whiteboard](/tools/whiteboard).

## Step 1: Extract Observations

Before you can synthesize, you need atomic observations. Go through each research session and pull out individual data points: things participants said, did, or felt. Write each observation on its own card or note on the canvas.

**Guidelines for good observations:**

- One observation per card, never combine multiple points
- Include a participant identifier so you can trace back to the source
- Use the participant's own words when possible rather than your interpretation
- Note the context: what task they were performing, what screen they were on
- Flag emotional reactions: frustration, delight, confusion, surprise

For a study with eight participants, you might end up with 150 to 300 observation cards. This volume is exactly why you need a large spatial workspace. Tools like OmniCanvas give you unlimited room to spread out and see everything at once.

## Step 2: Affinity Diagramming

With all your observations on the canvas, begin grouping them. This is affinity diagramming, and it is the backbone of qualitative research synthesis.

**The process:**

1. Read through the observations without moving anything first to get a sense of the data
2. Start picking up cards and placing them near other cards that seem related
3. Work silently if you are doing this with a team, at least for the first pass
4. Do not pre-define categories; let themes emerge from the data
5. When a cluster forms, give it a temporary label
6. Keep going until every card belongs to a cluster or is deliberately set aside as an outlier
7. Review the clusters and refine: split large clusters, merge small ones, rename as needed

### Common Pitfalls

- **Premature labeling:** Naming categories too early forces observations into boxes that may not fit. Let the clusters stabilize before labeling.
- **Ignoring outliers:** A single observation that does not fit any cluster might be the most important finding. Set it aside visibly rather than discarding it.
- **Over-grouping:** If a cluster has more than 15 cards, it is probably too broad. Look for sub-themes within it.

## Step 3: Generate Insights

Clusters of observations are not insights. An insight is a statement that **explains why** a pattern exists and **implies a design direction.** Compare these:

- **Observation cluster:** Five participants struggled to find the export button
- **Insight:** Users expect export functionality to live in the file menu because that matches their mental model from desktop applications; placing it in a toolbar icon breaks this expectation

The insight adds the "why" and suggests what to do about it. For each cluster on your canvas, write an insight statement and place it above or beside the cluster. Use a different color or size to distinguish insights from raw observations.

## Step 4: Journey Mapping on the Canvas

Once you have insights, a journey map helps you see how they relate to the user's experience over time. Create a horizontal timeline across a section of your canvas representing the key stages of the user's interaction with your product.

For each stage, add rows for:

- **Actions:** What the user does
- **Thoughts:** What the user is thinking
- **Emotions:** How the user feels, often shown as a simple curve from positive to negative
- **Pain points:** Specific problems identified in your research
- **Opportunities:** Design ideas that address the pain points

Place your insight cards from the affinity diagram into the appropriate stages of the journey map. This connects your bottom-up synthesis work with a top-down view of the experience.

## Step 5: Prioritize and Communicate

Synthesis is only valuable if it leads to action. On your canvas, create a simple prioritization framework next to your journey map. A common approach is a two-by-two matrix with **user impact** on one axis and **implementation effort** on the other. Place your key insights and opportunity cards into the quadrants.

High impact, low effort items become your immediate backlog. High impact, high effort items go into your roadmap. Low impact items get deprioritized or dropped.

## Presenting Your Synthesis

When you present to stakeholders, walk them through the canvas in order: raw observations, clusters, insights, journey map, priorities. This narrative shows your rigor and makes the conclusions feel earned rather than arbitrary. Stakeholders who see the path from data to decision are far more likely to trust and act on research findings.

## Keeping Synthesis Alive

Research synthesis should not be a one-time artifact. As you conduct more research rounds, add new observations to your canvas. See whether they reinforce existing clusters or reveal new patterns. Over time, your synthesis canvas becomes a **living knowledge base** of everything you know about your users, growing richer with each study.
`,
  },
  {
    slug: "sketchnoting-beginners-guide",
    title: "Sketchnoting for Beginners: Combine Drawing and Notetaking",
    description:
      "How to get started with sketchnoting even if you can't draw, including basic visual vocabulary (arrows, containers, icons, lettering).",
    date: "2026-07-12",
    readTime: "7 min read",
    category: "Creative",
    content: `
## What Is Sketchnoting?

Sketchnoting is a method of taking notes that combines **words, simple drawings, and spatial layout** to capture and communicate ideas. Unlike traditional notes that rely entirely on text, sketchnotes use visual elements like arrows, containers, icons, and hand-lettering to create a rich, memorable record of what you heard, read, or thought.

The most important thing to understand about sketchnoting is this: **it is not about artistic talent.** If you can draw a square, a circle, an arrow, and a stick figure, you have all the drawing ability you need. Sketchnoting is about thinking visually, not about creating gallery-worthy illustrations. It pairs naturally with other [visual brainstorming techniques](/blog/creative/visual-brainstorming-techniques), and you can practice the visual vocabulary below on any [online whiteboard](/tools/whiteboard).

## The Core Visual Vocabulary

Every sketchnoter works from a small set of reusable visual elements. Master these five building blocks and you can sketchnote anything.

### 1. Containers

Containers are shapes that hold text and give it visual weight. The most useful containers are:

- **Rectangles and rounded rectangles** for key concepts and definitions
- **Circles and ovals** for single words or short phrases you want to emphasize
- **Banners and ribbons** for titles and section headers
- **Speech bubbles** for direct quotes
- **Cloud shapes** for ideas, thoughts, or questions

Practice drawing each of these until they feel automatic. They do not need to be perfect. Slight irregularity actually makes sketchnotes feel more alive and personal.

### 2. Connectors

Connectors show relationships between ideas. Your toolkit includes:

- **Straight arrows** for cause and effect or sequence
- **Curved arrows** for flow and movement
- **Dotted lines** for tentative or weak connections
- **Numbered paths** for step-by-step processes
- **Branching lines** for hierarchies and mind-map structures

### 3. Text and Lettering

Vary your lettering to create visual hierarchy:

- **Large, bold letters** for titles and main ideas
- **Medium print** for supporting points
- **Small handwriting** for details and annotations
- **ALL CAPS** for emphasis on key terms
- **Underlines and highlights** for critical takeaways

You do not need fancy calligraphy. Simple, legible print in two or three sizes creates enough hierarchy to make your notes scannable.

### 4. Simple Icons

Icons are tiny drawings that represent concepts. You only need about 20 to cover most situations:

- **People:** Stick figures or simple head-and-shoulders outlines
- **Communication:** Speech bubbles, envelopes, phones
- **Time:** Clocks, calendars, hourglasses
- **Growth:** Arrows pointing up, bar charts, seedlings
- **Ideas:** Light bulbs, stars, exclamation marks
- **Problems:** Warning triangles, X marks, storm clouds
- **Tools:** Gears, wrenches, computers, pencils

Start a personal icon library on a reference page. Every time you figure out a simple way to draw a concept, add it to your library. Over time, this becomes your visual shorthand.

### 5. Dividers and Separators

Use horizontal lines, dotted borders, or shaded bars to separate sections of your sketchnotes. This prevents everything from blending into visual noise and helps readers navigate the page.

## Layout Strategies

How you arrange elements on the page matters as much as what you draw. Here are four common sketchnote layouts:

**Linear flow:** Work from top-left to bottom-right, like reading a page. Best for sequential content like lectures or presentations with a clear beginning, middle, and end.

**Radial layout:** Place the main topic in the center and branch outward. Best for topics with several equal sub-themes that do not follow a strict sequence.

**Columns:** Divide the page into two or three vertical columns. Best for comparing ideas or capturing multiple speakers at a panel discussion.

**Modular:** Create distinct rectangular sections scattered across the page, each containing a self-contained idea. Best for conferences or brainstorming sessions where topics jump around unpredictably. An infinite canvas like OmniCanvas is especially well-suited for modular layouts because you never run out of room.

## Sketchnoting in Real Time

Taking sketchnotes during a live talk or meeting requires a slightly different approach than sketchnoting from a book or article.

**Before the event:**

1. Write the title, speaker name, and date at the top of your page
2. Listen for the overall structure in the first few minutes before drawing much
3. Identify whether the content is sequential, categorical, or argumentative

**During the event:**

1. Capture key phrases, not full sentences
2. Draw containers around the most important points as they happen
3. Leave empty space; you can fill in details afterward
4. Use arrows and lines to connect related points as the talk progresses
5. Do not worry about mistakes; sketchnotes are personal records, not publications

**After the event:**

1. Review your notes within 24 hours while your memory is fresh
2. Add details, fix unclear sections, and strengthen connections
3. Add color if you want, but it is entirely optional

## Why Sketchnoting Works

Sketchnoting engages what researchers call **dual coding:** processing information through both verbal and visual channels simultaneously. Studies show that people who take visual notes retain 29 percent more information than those who take text-only notes. The act of deciding how to visually represent an idea forces deeper processing than simply transcribing words.

Beyond retention, sketchnotes are **enjoyable to review.** Traditional notes often feel like a chore to revisit. Sketchnotes invite you back in because they are visually interesting and spatially organized. You can scan a sketchnote page and find what you need in seconds rather than reading through paragraphs of text.

You do not need special tools or special talent. You just need a willingness to pick up a pen and start drawing boxes around your ideas.
`,
  },
  {
    slug: "plan-creative-project-canvas",
    title: "How to Plan a Creative Project on an Infinite Canvas",
    description:
      "A framework for planning creative projects (albums, films, books, apps) on infinite canvases with phases, milestones, and reference materials.",
    date: "2026-07-19",
    readTime: "7 min read",
    category: "Creative",
    content: `
## Creative Projects Need a Different Kind of Plan

Creative projects are not like business projects. Building a product has a relatively predictable path: requirements, design, build, test, ship. Writing an album, making a short film, illustrating a graphic novel, or designing an indie game follows a messier trajectory. Ideas emerge and recombine. Inspiration arrives at unpredictable moments. The final output often looks nothing like the initial concept.

Traditional project management tools struggle with this reality. Gantt charts and kanban boards assume you know the tasks upfront. Spreadsheets are lifeless. What creative projects need is a planning surface that is **flexible, visual, and spatially unlimited.** An infinite canvas provides exactly this. The planning approach below builds on the same [visual brainstorming techniques](/blog/creative/visual-brainstorming-techniques) creatives use to generate ideas, all on a single [online whiteboard](/tools/whiteboard).

## The Five-Zone Framework

Here is a framework you can adapt to virtually any creative project. It divides your canvas into five spatial zones, each serving a distinct purpose. You can arrange them however feels natural, but many people place them in a rough left-to-right flow.

### Zone 1: Vision and Brief

This zone answers the question: **what are we making and why?**

Include the following elements:

- **Project title and one-sentence description**
- **Target audience:** Who is this for?
- **Core emotion or experience:** What should the audience feel?
- **Constraints:** Budget, timeline, technical limitations, format requirements
- **Success criteria:** How will you know the project is done and good?
- **Anti-goals:** What this project is explicitly not trying to be

Keep this zone concise. It is your north star, and you should be able to read it in under two minutes. When you feel lost in the weeds of production, zoom into this zone and recalibrate.

### Zone 2: Inspiration and References

This is your mood board and reference library. Collect everything that informs the project's aesthetic, tone, and approach:

- Images, color palettes, and visual references
- Links to or notes on similar works you admire
- Quotes, lyrics, or passages that capture the right feeling
- Technical references: how-to guides, tutorials, specifications
- Audience research and market context

Organize references into sub-clusters by theme. A filmmaker might have separate clusters for cinematography references, sound design references, and costume references. A game designer might group references by art style, game mechanics, and narrative tone.

### Zone 3: Structure and Outline

This zone contains the architecture of your project. Its contents vary by project type:

- **For a book:** Chapter outline, character map, plot timeline, theme tracker
- **For an album:** Track list, song sketches, key and tempo notes, lyrical themes
- **For a film:** Scene breakdown, shot list, location notes, casting ideas
- **For an app:** Feature map, user flows, screen inventory, technical architecture
- **For a visual art series:** Piece inventory, size and medium notes, exhibition layout

The structure zone is where you do the hard thinking about how all the pieces fit together. It connects directly to your vision zone (does this structure serve the vision?) and your reference zone (does the structure reflect your influences?).

### Zone 4: Production Tracker

This zone tracks the actual making of the project. Create a simple status system:

- **Not started:** Ideas and tasks that have been identified but not begun
- **In progress:** Work that is actively being done
- **In review:** Work that needs feedback or revision
- **Complete:** Finished pieces

For each item, note any dependencies, deadlines, and who is responsible if you are collaborating. You do not need a sophisticated tool for this. Simple colored labels or a spatial arrangement where items move from left to right as they progress works well on a canvas.

### Zone 5: Archive and Iterations

Creative projects generate a lot of discarded material: early drafts, abandoned ideas, alternate versions. Do not delete them. Move them to the archive zone. This serves two purposes. First, you might revisit an abandoned idea later and find it works in a different context. Second, the archive becomes a record of your creative process, which is valuable for learning and for showing your work.

Within the archive, organize loosely by date or project phase. Add brief notes explaining why something was set aside. "Cut for pacing" is more useful than a naked discarded draft with no context.

## Working the Framework in Practice

Start a new project by creating all five zones on OmniCanvas or your preferred infinite canvas tool. Spend your first session filling in Zone 1 and Zone 2. Do not rush into structure until you have a clear vision and rich reference material.

As the project progresses, you will naturally spend more time in Zones 3 and 4. The key discipline is to **keep all five zones updated.** When your vision shifts, update Zone 1. When you discover new inspiration, add it to Zone 2. When you cut something from the structure, move it to Zone 5.

## Milestones and Check-Ins

Even messy creative projects benefit from milestones. Define three to five major milestones for your project and place them on a simple timeline in or near your production zone. Examples:

1. Vision and references complete
2. Structure and outline locked
3. First draft or rough cut complete
4. Revision and polish complete
5. Final delivery or release

At each milestone, zoom out to see your entire canvas. Ask yourself: does the work in the production zone still align with the vision zone? Have the references evolved in a way that suggests the vision needs updating? This birds-eye review is one of the greatest advantages of planning on an infinite canvas. You see the whole project in a single view, connections and all, rather than flipping between tabs and documents.

## Collaboration on the Canvas

If you are working with a team, the five-zone framework gives everyone a shared spatial language. Designers know where to find references. Producers know where to check status. Writers know where the structural outline lives. The canvas becomes the single source of truth for the project, replacing scattered documents, chat threads, and email attachments.

Creative work is inherently non-linear. Your planning tool should be too.
`,
  },
];
